import React from 'react';
import { Card, notification } from 'antd';
import {
  DigitalTwin,
  Milestone,
  useDeleteDigitalTwinMutation,
  useUpdateDigitalTwinMutation,
} from 'types/dt-graphql-apollo-v2';
import mixpanel from 'utils/mixpanel-frontend';
import { useUrl } from 'hooks/useUrls';
import { getDigitalTwinUrl } from 'utils/router-url';
import { CardItem } from 'components/CardItem/CardItem';
import { EmptyCard } from 'components/CardItem/EmptyCard';
import { useStatefulHistory } from 'hooks/history';
import {
  getAllMilestoneCollections,
  getEarliestDateFromMilestones,
  getLatestDateFromMilestones,
  getMilestoneStatus,
  MilestoneStatus,
} from 'utils/project-ruleset';
import styled from 'styled-components/macro';
import { LabelListDisplay } from 'components';
import { useAccessControl } from 'hooks/useAccessControl';

type DigitalTwinListTypes = {
  digitalTwins: Array<DigitalTwin>;
  onCreateNewDigitalTwin: VoidFunction;
  onUpdate?: VoidFunction;
};

export const DigitalTwinsLoadingCards = () => {
  const loadingCardKeys = [0, 1, 2, 3, 4, 5];
  return (
    <StyledCardsList>
      {loadingCardKeys.map((key) => (
        <StyledCard key={key}>
          <Card loading />
        </StyledCard>
      ))}
    </StyledCardsList>
  );
};

export const DigitalTwinsCards = ({
  digitalTwins,
  onCreateNewDigitalTwin,
  onUpdate = () => {},
}: DigitalTwinListTypes) => {
  const [updateDigitalTwin] = useUpdateDigitalTwinMutation();
  const [deleteDigitalTwin] = useDeleteDigitalTwinMutation();

  const tenant = useUrl.Tenant();
  const history = useStatefulHistory();

  const hasAccess = useAccessControl();
  const canEditProject = hasAccess(['Update_DigitalTwin']);
  const canDeleteProject = hasAccess(['Delete_DigitalTwin']);

  const onOpen = (digitalTwin: DigitalTwin) => {
    const { externalId: digitalTwinId, name: digitalTwinName } = digitalTwin;
    mixpanel.track('Opened project', {
      digitalTwinId,
      digitalTwinName,
    });
    history.pushStateful(`${getDigitalTwinUrl(tenant, digitalTwinId)}/schemas`);
  };

  const onDelete = async (digitalTwin: DigitalTwin) => {
    const { externalId: digitalTwinId, name: digitalTwinName } = digitalTwin;
    try {
      await deleteDigitalTwin({
        variables: { id: digitalTwinId },
      });

      notification.success({
        message: `Successfully deleted project!`,
        description: `The project "${digitalTwinName}" was deleted.`,
      });
      mixpanel.track(`Deleted project`, { id: digitalTwinId });
      onUpdate();
    } catch (error: any) {
      mixpanel.track(`Could not delete project`, {
        id: digitalTwinId,
        error: error.toString(),
      });
      notification.error({
        message: `Could not delete project`,
        description: error.toString(),
      });
    }
  };

  const onRename = async (digitalTwin: DigitalTwin, newName: string) => {
    try {
      await updateDigitalTwin({
        variables: {
          id: digitalTwin.externalId,
          name: newName,
          tenant,
        },
      });
      onUpdate();
      mixpanel.track(`Renamed project`, { id: digitalTwin.externalId });
    } catch (error: any) {
      notification.error({
        message: `Could not rename project`,
        description: error.toString(),
      });
    }
  };

  if (digitalTwins.length === 0) {
    return (
      <EmptyCard
        title="Create project"
        onCreateNewItem={onCreateNewDigitalTwin}
      />
    );
  }

  const getMilestones = (digitalTwin: DigitalTwin) =>
    digitalTwin?.schemaCollections &&
    digitalTwin?.schemaCollections?.items.length > 0 &&
    (digitalTwin?.schemaCollections.items[0]?.milestones?.items as Milestone[]); // just treat it as a project has only one schema for now

  const renderProjectTimeline = (digitalTwin: DigitalTwin) => {
    const milestones = getMilestones(digitalTwin);

    if (!milestones) return 'No project timeline';
    const earliestDateFromMilestones =
      getEarliestDateFromMilestones(milestones);
    const latestDateFromMilestones = getLatestDateFromMilestones(milestones);
    if (!earliestDateFromMilestones || !latestDateFromMilestones)
      return 'No project timeline';
    return `${earliestDateFromMilestones} to ${latestDateFromMilestones}`;
  };

  const renderMilestoneTags = (milestones: Milestone[]) => (
    <>
      {milestones?.length ? (
        <LabelListDisplay
          labels={milestones.map((m) => ({ text: m.name, key: m.externalId }))}
        />
      ) : (
        'No active milestones'
      )}
    </>
  );

  const renderActiveMilestones = (digitalTwin: DigitalTwin) => {
    const milestones = getMilestones(digitalTwin);
    const filteredMilestones = getAllMilestoneCollections(milestones || []);

    if (!filteredMilestones) return 'No active milestones';
    const activeMilestones = filteredMilestones.filter(
      (m) =>
        getMilestoneStatus(filteredMilestones, m) === MilestoneStatus.ACTIVE
    );
    return renderMilestoneTags(activeMilestones);
  };
  const renderSchemasCount = (digitalTwin: DigitalTwin) => {
    const schemaCount = digitalTwin.schemaCollections?.items.length;
    return (
      <StyledSpanItalic>
        {`${schemaCount} ${schemaCount === 1 ? 'schema' : 'schemas'}`}
      </StyledSpanItalic>
    );
  };

  const renderDigitalTwinCard = (digitalTwin: DigitalTwin) => (
    <StyledCard key={digitalTwin.externalId}>
      <CardItem
        itemType="Project"
        itemName={digitalTwin.name}
        title={digitalTwin.name}
        key={digitalTwin.externalId}
        onOpen={() => onOpen(digitalTwin)}
        contentList={[
          {
            key: 'schemaCount',
            heading: renderSchemasCount(digitalTwin),
          },
          {
            key: 'projectTimeline',
            heading: 'PROJECT TIMELINE',
            details: renderProjectTimeline(digitalTwin),
          },
          {
            key: 'activeMilestone',
            heading: 'ACTIVE MILESTONE',
            details: renderActiveMilestones(digitalTwin),
          },
        ]}
        editAvailable={canEditProject}
        deleteAvailable={canDeleteProject}
        onDelete={() => onDelete(digitalTwin)}
        onRename={(newName: string) => onRename(digitalTwin, newName)}
      />
    </StyledCard>
  );

  return (
    <StyledCardsList>
      {digitalTwins?.map((digitalTwin) => renderDigitalTwinCard(digitalTwin))}
    </StyledCardsList>
  );
};

const StyledSpanItalic = styled.span`
  font-style: italic;
  font-weight: normal;
`;

const StyledCard = styled.div`
  width: 100%;
  height: 100%;
  flex: 1 auto;
  display: flex;
  > .ant-card {
    width: 100%;
  }
`;

const StyledCardsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  align-content: stretch;
  justify-items: stretch;
  flex-wrap: wrap;
  gap: 1rem !important;
  margin-bottom: 1rem;
`;
